import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface BonTransfer {
  id: string;
  numar: string;
  data: string;
  deLaGestiune: string;
  laGestiune: string;
  deLaParohie: string;
  laParohie: string;
  produse: Array<{
    nume: string;
    cantitate: number;
    unitate: string;
    valoareUnitar: number;
    valoareTotal: number;
  }>;
  valoareTotala: number;
  motiv: string;
  status: 'in_asteptare' | 'efectuat' | 'anulat';
  observatii?: string;
  aprobatDe?: string;
  dataAprobare?: string;
}

const BonuriTransfer: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBon, setSelectedBon] = useState<BonTransfer | null>(null);

  // Mock data pentru bonuri transfer
  const [bonuriTransfer] = useState<BonTransfer[]>([
    {
      id: 'bt1',
      numar: 'BT-001/2024',
      data: '2024-01-15',
      deLaGestiune: 'Depozit Episcopie',
      laGestiune: 'Gestiunea Parohiei Sf. Nicolae',
      deLaParohie: 'Episcopia Ortodoxă Română din Italia',
      laParohie: 'Parohia Sf. Nicolae',
      produse: [
        {
          nume: 'Lumânări de ceară',
          cantitate: 100,
          unitate: 'buc',
          valoareUnitar: 5,
          valoareTotal: 500
        },
        {
          nume: 'Lumânări decorative',
          cantitate: 50,
          unitate: 'buc',
          valoareUnitar: 8,
          valoareTotal: 400
        }
      ],
      valoareTotala: 900,
      motiv: 'Provizii pentru sărbătorile de iarnă',
      status: 'efectuat',
      observatii: 'Transfer efectuat cu succes',
      aprobatDe: 'Părintele Gheorghe',
      dataAprobare: '2024-01-15'
    },
    {
      id: 'bt2',
      numar: 'BT-002/2024',
      data: '2024-01-20',
      deLaGestiune: 'Depozit Episcopie',
      laGestiune: 'Gestiunea Parohiei Sf. Gheorghe',
      deLaParohie: 'Episcopia Ortodoxă Română din Italia',
      laParohie: 'Parohia Sf. Gheorghe',
      produse: [
        {
          nume: 'Lumânări de ceară',
          cantitate: 200,
          unitate: 'buc',
          valoareUnitar: 5,
          valoareTotal: 1000
        },
        {
          nume: 'Lumânări pentru altar',
          cantitate: 20,
          unitate: 'buc',
          valoareUnitar: 15,
          valoareTotal: 300
        }
      ],
      valoareTotala: 1300,
      motiv: 'Provizii pentru Paște',
      status: 'efectuat',
      observatii: 'Pentru sărbătorile de Paște',
      aprobatDe: 'Părintele Mihai',
      dataAprobare: '2024-01-20'
    },
    {
      id: 'bt3',
      numar: 'BT-003/2024',
      data: '2024-01-25',
      deLaGestiune: 'Depozit Episcopie',
      laGestiune: 'Gestiunea Parohiei Sf. Dumitru',
      deLaParohie: 'Episcopia Ortodoxă Română din Italia',
      laParohie: 'Parohia Sf. Dumitru',
      produse: [
        {
          nume: 'Lumânări de ceară',
          cantitate: 150,
          unitate: 'buc',
          valoareUnitar: 5,
          valoareTotal: 750
        },
        {
          nume: 'Lumânări decorative',
          cantitate: 30,
          unitate: 'buc',
          valoareUnitar: 8,
          valoareTotal: 240
        },
        {
          nume: 'Lumânări pentru iconostas',
          cantitate: 10,
          unitate: 'buc',
          valoareUnitar: 12,
          valoareTotal: 120
        }
      ],
      valoareTotala: 1110,
      motiv: 'Provizii pentru sărbătorile de vară',
      status: 'in_asteptare',
      observatii: 'În așteptarea aprobării'
    },
    {
      id: 'bt4',
      numar: 'BT-004/2024',
      data: '2024-01-28',
      deLaGestiune: 'Depozit Episcopie',
      laGestiune: 'Gestiunea Parohiei Sf. Maria',
      deLaParohie: 'Episcopia Ortodoxă Română din Italia',
      laParohie: 'Parohia Sf. Maria',
      produse: [
        {
          nume: 'Lumânări de ceară',
          cantitate: 80,
          unitate: 'buc',
          valoareUnitar: 5,
          valoareTotal: 400
        },
        {
          nume: 'Lumânări pentru altar',
          cantitate: 15,
          unitate: 'buc',
          valoareUnitar: 15,
          valoareTotal: 225
        }
      ],
      valoareTotala: 625,
      motiv: 'Provizii pentru sărbătorile de toamnă',
      status: 'efectuat',
      observatii: 'Transfer completat',
      aprobatDe: 'Părintele Vasile',
      dataAprobare: '2024-01-28'
    }
  ]);

  const filteredBonuri = bonuriTransfer.filter(bon => {
    const matchesSearch = bon.numar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bon.deLaGestiune.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bon.laGestiune.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bon.deLaParohie.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bon.laParohie.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bon.motiv.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || bon.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const totalEfectuate = bonuriTransfer
    .filter(b => b.status === 'efectuat')
    .reduce((sum, b) => sum + b.valoareTotala, 0);

  const totalInAsteptare = bonuriTransfer
    .filter(b => b.status === 'in_asteptare')
    .reduce((sum, b) => sum + b.valoareTotala, 0);

  const totalGeneral = bonuriTransfer.reduce((sum, b) => sum + b.valoareTotala, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'efectuat': return 'success';
      case 'in_asteptare': return 'warning';
      case 'anulat': return 'error';
      default: return 'default';
    }
  };

  const handleViewBon = (bon: BonTransfer) => {
    setSelectedBon(bon);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBon(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Bonuri Transfer
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare bon nou */}}
          >
            Bon nou
          </Button>
        )}
      </Box>

      {/* Statistici bonuri */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ✅
                </Typography>
                <Typography color="textSecondary">
                  Efectuate
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalEfectuate.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ⏳
                </Typography>
                <Typography color="textSecondary">
                  În așteptare
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                €{totalInAsteptare.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Total general
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                €{totalGeneral.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru bonuri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Caută bonuri..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="in_asteptare">În așteptare</MenuItem>
                <MenuItem value="efectuat">Efectuat</MenuItem>
                <MenuItem value="anulat">Anulat</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabel bonuri */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Număr</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>De la Gestiune</TableCell>
              <TableCell>La Gestiune</TableCell>
              <TableCell>Parohii</TableCell>
              <TableCell>Produse</TableCell>
              <TableCell>Valoare</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBonuri.map((bon) => (
              <TableRow key={bon.id}>
                <TableCell>{bon.numar}</TableCell>
                <TableCell>{bon.data}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {bon.deLaGestiune}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bon.deLaParohie}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {bon.laGestiune}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bon.laParohie}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {bon.deLaParohie} → {bon.laParohie}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {bon.produse.length} produse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bon.produse.map(p => p.nume).join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">
                    €{bon.valoareTotala.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={bon.status}
                    color={getStatusColor(bon.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewBon(bon)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton color="default">
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare bon */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii bon transfer
        </DialogTitle>
        <DialogContent>
          {selectedBon && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Număr bon
                </Typography>
                <Typography variant="body1">
                  {selectedBon.numar}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedBon.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  De la Gestiune
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedBon.deLaGestiune}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedBon.deLaParohie}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  La Gestiune
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedBon.laGestiune}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedBon.laParohie}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Produse transferate
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produs</TableCell>
                      <TableCell>Cantitate</TableCell>
                      <TableCell>Valoare unitară</TableCell>
                      <TableCell>Valoare totală</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedBon.produse.map((produs, index) => (
                      <TableRow key={index}>
                        <TableCell>{produs.nume}</TableCell>
                        <TableCell>{produs.cantitate} {produs.unitate}</TableCell>
                        <TableCell>€{produs.valoareUnitar.toLocaleString()}</TableCell>
                        <TableCell>€{produs.valoareTotal.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Valoare totală
                </Typography>
                <Typography variant="h5" color="primary.main">
                  €{selectedBon.valoareTotala.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedBon.status}
                  color={getStatusColor(selectedBon.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Motiv
                </Typography>
                <Typography variant="body1">
                  {selectedBon.motiv}
                </Typography>
              </Grid>
              {selectedBon.aprobatDe && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Aprobat de
                  </Typography>
                  <Typography variant="body1">
                    {selectedBon.aprobatDe}
                  </Typography>
                </Grid>
              )}
              {selectedBon.dataAprobare && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Data aprobare
                  </Typography>
                  <Typography variant="body1">
                    {selectedBon.dataAprobare}
                  </Typography>
                </Grid>
              )}
              {selectedBon.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedBon.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BonuriTransfer;
